import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { CheckCircle2, XCircle, Clock } from 'lucide-react';
import { WorkflowRun, WorkflowRunStatus } from '@/types/agents';

interface RunDetailsModalProps {
    run: WorkflowRun;
    isOpen: boolean;
    onClose: () => void;
}

const RunDetailsModal: React.FC<RunDetailsModalProps> = ({ run, isOpen, onClose }) => {

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    };

    const formatDuration = (seconds?: number) => {
        if (!seconds) return 'N/A';
        if (seconds < 60) return `${seconds}s`;
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds}s`;
    };

    const getStatusIcon = (status: WorkflowRunStatus) => {
        switch (status) {
            case 'Successful':
                return <CheckCircle2 size={16} className="text-secondary" />;
            case 'Failed':
                return <XCircle size={16} className="text-destructive" />;
            case 'Running':
                return <Clock size={16} className="text-primary" />;
            default:
                return null;
        }
    };

    const getStatusColor = (status: WorkflowRunStatus) => {
        switch (status) {
            case 'Successful':
                return 'hsl(var(--secondary))';
            case 'Failed':
                return 'hsl(var(--destructive))';
            case 'Running':
                return 'hsl(var(--primary))';
            default:
                return 'hsl(var(--muted-foreground))';
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-foreground">
                        Workflow Run Details
                    </DialogTitle>
                    <DialogDescription className="text-muted-foreground">
                        {run.workflowName}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 mt-4">
                    {/* Overview */}
                    <div>
                        <h3 className="text-lg font-semibold mb-3 text-foreground">
                            Overview
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-muted-foreground">
                                    Date
                                </p>
                                <p className="font-medium text-foreground">
                                    {formatDate(run.date)}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">
                                    Status
                                </p>
                                <div className="flex items-center gap-2">
                                    {getStatusIcon(run.status)}
                                    <span
                                        className="font-medium"
                                        style={{ color: getStatusColor(run.status) }}
                                    >
                                        {run.status}
                                    </span>
                                </div>
                            </div>
                            {run.details?.startTime && (
                                <div>
                                    <p className="text-sm text-muted-foreground">
                                        Start Time
                                    </p>
                                    <p className="font-medium text-foreground">
                                        {formatDate(run.details.startTime)}
                                    </p>
                                </div>
                            )}
                            {run.details?.endTime && (
                                <div>
                                    <p className="text-sm text-muted-foreground">
                                        End Time
                                    </p>
                                    <p className="font-medium text-foreground">
                                        {formatDate(run.details.endTime)}
                                    </p>
                                </div>
                            )}
                            {run.details?.duration !== undefined && (
                                <div>
                                    <p className="text-sm text-muted-foreground">
                                        Duration
                                    </p>
                                    <p className="font-medium text-foreground">
                                        {formatDuration(run.details.duration)}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Message */}
                    <div>
                        <h3 className="text-lg font-semibold mb-3 text-foreground">
                            Message
                        </h3>
                        <p className="text-muted-foreground">{run.message}</p>
                    </div>

                    {/* Error */}
                    {run.details?.error && (
                        <div>
                            <h3 className="text-lg font-semibold mb-3 text-foreground">
                                Error
                            </h3>
                            <div
                                className="p-3 rounded-md"
                                style={{
                                    backgroundColor: "hsl(var(--secondary) / 0.1)",
                                    border: "1px solid hsl(var(--secondary))",
                                }}
                            >
                                <p className="text-secondary">{run.details.error}</p>
                            </div>
                        </div>
                    )}

                    {/* Steps */}
                    {run.details?.steps && run.details.steps.length > 0 && (
                        <div>
                            <h3 className="text-lg font-semibold mb-3 text-foreground">
                                Workflow Steps
                            </h3>
                            <div className="space-y-2">
                                {run.details.steps.map((step: any, index: number) => (
                                    <div
                                        key={step.id}
                                        className="p-3 rounded-md border"
                                        style={{
                                            backgroundColor: "hsl(var(--background))",
                                            borderColor: "hsl(var(--muted-foreground))",
                                        }}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-foreground">
                                                    {index + 1}. {step.name}
                                                </span>
                                                {getStatusIcon(step.status)}
                                            </div>
                                            {step.duration !== undefined && (
                                                <span className="text-xs text-muted-foreground">
                                                    {formatDuration(step.duration)}
                                                </span>
                                            )}
                                        </div>
                                        {step.error && (
                                            <p className="text-xs mt-1 text-secondary">
                                                {step.error}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default RunDetailsModal;

